import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_BRANDING = {
  platform_name: 'ATÔ',
  tagline: 'Là où Madagascar achète et vend',
  logo_path: null,
  favicon_path: null,
  banner_path: null,
  banner_headline: null,
  colors: {
    primary: '#2E6F57',
    primary_hover: '#245741',
    accent: '#E76F3C',
    accent_hover: '#D45F2C',
    danger: '#C0392B',
    danger_hover: '#A93226',
    background: '#F5F3EF',
  },
  fonts: {
    heading: 'Poppins',
    body: 'Inter',
  },
};

// Font families known to Phase 2. Anything outside this set is ignored —
// matches the backend whitelist exactly.
const ALLOWED_FONTS = new Set(['Poppins', 'Inter', 'Montserrat', 'Roboto', 'Open Sans', 'Lato']);

// Google Fonts CSS2 weights we consistently load for every family — covers
// 400 (body), 500 (medium), 600 (semibold), 700 (bold), 800 (extrabold).
const FONT_WEIGHTS = '400;500;600;700;800';

const ensureGoogleFontLoaded = (family) => {
  if (typeof document === 'undefined') return;
  if (!family || !ALLOWED_FONTS.has(family)) return;
  const id = `google-font-${family.replace(/\s+/g, '-').toLowerCase()}`;
  if (document.getElementById(id)) return; // already injected
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, '+')}:wght@${FONT_WEIGHTS}&display=swap`;
  document.head.appendChild(link);
};

const BrandingContext = createContext({
  branding: DEFAULT_BRANDING,
  refresh: () => {},
  loaded: false,
});

const applyBranding = (b) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const c = b?.colors || {};
  if (c.primary) root.style.setProperty('--brand-primary', c.primary);
  if (c.primary_hover) root.style.setProperty('--brand-primary-hover', c.primary_hover);
  if (c.accent) root.style.setProperty('--brand-accent', c.accent);
  if (c.accent_hover) root.style.setProperty('--brand-accent-hover', c.accent_hover);
  if (c.danger) root.style.setProperty('--brand-danger', c.danger);
  if (c.danger_hover) root.style.setProperty('--brand-danger-hover', c.danger_hover);
  if (c.background) root.style.setProperty('--brand-background', c.background);

  // Fonts — inject CSS variables and lazy-load the Google Font stylesheet.
  // Quoted to remain valid in `font-family` lists with spaces (e.g. "Open Sans").
  const f = b?.fonts || {};
  if (f.heading && ALLOWED_FONTS.has(f.heading)) {
    root.style.setProperty('--font-heading', `"${f.heading}"`);
    ensureGoogleFontLoaded(f.heading);
  }
  if (f.body && ALLOWED_FONTS.has(f.body)) {
    root.style.setProperty('--font-body', `"${f.body}"`);
    ensureGoogleFontLoaded(f.body);
  }

  // Document title is owned by the i18n root (App.js) so it stays in sync
  // with the active language. Branding no longer overrides it here.

  // Favicon — swap <link rel="icon"> (create one if missing)
  if (b?.favicon_path) {
    const href = b.favicon_path.startsWith('http')
      ? b.favicon_path
      : `${API}/files/${b.favicon_path}`;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = href;
  }
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/branding`);
      setBranding(data);
      applyBranding(data);
    } catch (err) {
      // Keep defaults if backend is unreachable — don't block app boot
      console.debug('Branding fetch failed, using defaults', err?.message);
      applyBranding(DEFAULT_BRANDING);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <BrandingContext.Provider value={{ branding, refresh, loaded }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => useContext(BrandingContext);

export const BRANDING_FONTS = ['Poppins', 'Inter', 'Montserrat', 'Roboto', 'Open Sans', 'Lato'];
